import { Fragment } from 'react'

import { formatDateRange, formatPrice, monogramColors, safeUrl, timeAgo } from '../../../../format'

import style from './style.module.css'

/**
 * One event row. There is no photo per event — the circular slot carries the
 * organisation monogram instead, which is also why the profile_picture.png
 * import is gone.
 *
 * The metadata line leads with the relative time ("in 12 days" / "3 weeks ago")
 * because an absolute date alone reads as upcoming to anyone skimming: an
 * "August 28, 2026" sitting in a list is indistinguishable from a save-the-date
 * until you work out today's date yourself.
 */
const EventItem = ({ event, initials, orgKey }) => {
  const href = safeUrl(event.url)
  const colors = monogramColors(orgKey)

  const isUpcoming = Boolean(event.isUpcoming)
  const relative = timeAgo(event.startAt)
  const absolute = formatDateRange(event.startAt, event.endAt)
  const price = formatPrice(event.price, event.currency, event.isFree)

  // Built as a list so the separators only appear between parts that exist —
  // any of the three can be missing on a scraped row.
  const metaParts = []
  if (relative) {
    metaParts.push({
      key: 'relative',
      node: (
        <span
          className={
            isUpcoming
              ? `${style.companyModal_events_item_when} ${style.companyModal_events_item_when_upcoming}`
              : style.companyModal_events_item_when
          }
        >
          {relative}
        </span>
      ),
    })
  }
  if (absolute) metaParts.push({ key: 'absolute', node: absolute })
  if (event.location) metaParts.push({ key: 'location', node: event.location })

  const itemClass = isUpcoming
    ? style.companyModal_events_item
    : `${style.companyModal_events_item} ${style.companyModal_events_item_past}`

  const body = (
    <>
      <span
        className={style.companyModal_events_item_photo}
        style={{
          backgroundColor: colors.background,
          color: colors.ink,
          boxShadow: `inset 0 0 0 1.5px ${colors.foreground}`,
        }}
        aria-hidden="true"
      >
        {initials}
      </span>

      <span className={style.companyModal_events_item_text}>
        {event.title && (
          <span className={style.companyModal_events_item_title}>{event.title}</span>
        )}

        {metaParts.length > 0 && (
          <span className={style.companyModal_events_item_meta}>
            {isUpcoming && (
              <span className={style.companyModal_events_item_dot} aria-hidden="true" />
            )}

            {metaParts.map((part, index) => (
              <Fragment key={part.key}>
                {index > 0 && (
                  <span className={style.companyModal_events_item_sep} aria-hidden="true">
                    ·
                  </span>
                )}
                {part.node}
              </Fragment>
            ))}
          </span>
        )}
      </span>

      {/* An upcoming event gets an action, not a price tag. "Free" told a reader
          nothing they could act on; the whole row is the link, so this is the
          affordance that says so. Past events keep the price, since there is
          nothing left to register for. */}
      {isUpcoming ? (
        <span className={style.companyModal_events_item_cta}>Register</span>
      ) : (
        price && <span className={style.companyModal_events_item_price}>{price}</span>
      )}
    </>
  )

  if (!href) {
    return <div className={itemClass}>{body}</div>
  }

  return (
    <a
      className={`${itemClass} ${style.companyModal_events_item_link}`}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-log-action="spotlight_open_event"
    >
      {body}
    </a>
  )
}

export default EventItem
